// import { CommonCursorPagination } from "@/components/common/CommonCursorPagination";
import { CommonPagination } from "@/components/common/CommonPagination";
import Loading from "@/components/common/Loading";
import {
  Accordion,
  AccordionContent,
  AccordionItem,
  AccordionTrigger,
} from "@/components/ui/Accordion";
import { Badge } from "@/components/ui/Badge";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/Table";
import { QUERY_KEY } from "@/constant/queryKey";
// import { useCursorPagination } from "@/hooks/use-cursor-pagination";
import { usePagination } from "@/hooks/use-pagination";
// import { useUrlCursorParams } from "@/hooks/use-url-cursor-params";
import { useUrlParams } from "@/hooks/use-url-params";
import { prettyFixedAmount } from "@/lib/utils";
import {
  Transaction,
  TRANSACTION_ENDPOINT,
} from "@/services/core/transaction.api";
import { useMerchantStore } from "@/store/merchant-store";
import { format } from "date-fns";
import { useState } from "react";

const ViewLogsPage = () => {
  const { params, updateParams } = useUrlParams();
  // const { params, updateParams } = useUrlCursorParams();
  const { selectedMerchant } = useMerchantStore();

  // const {
  //   response,
  //   data: transactions,
  //   isLoading,
  //   isError,
  //   meta,
  //   goToNext,
  //   goToPrevious,
  // } = useCursorPagination<Transaction>({
  //   endpoint: `${TRANSACTION_ENDPOINT}/${selectedMerchant?.name}/list`,
  //   queryKey: [QUERY_KEY.TRANSACTIONS, selectedMerchant?.name ?? ""],
  //   initialParams: params,
  // });

  const {
    data: transactions,
    isLoading,
    isError,
    meta,
    setPage,
    setLimit,
  } = usePagination<Transaction>({
    endpoint: `${TRANSACTION_ENDPOINT}/${selectedMerchant?.name}/list`,
    queryKey: [QUERY_KEY.TRANSACTIONS, selectedMerchant?.name ?? ""],
    initialParams: {
      currentPage: params.currentPage,
      pageSize: params.pageSize,
      q: params.q,
    },
  });

  const [openItems, setOpenItems] = useState<string[]>([]);

  // const handlePaginationChange = (direction: "next" | "prev") => {
  //   const { prevCursor, nextCursor } = meta;
  //   updateParams({
  //     [direction === "next" ? "after" : "before"]:
  //       direction === "next" ? nextCursor : prevCursor,
  //     [direction === "next" ? "before" : "after"]: null,
  //     pageSize: 10,
  //   });

  //   if (direction === "next") {
  //     goToNext();
  //   } else {
  //     goToPrevious();
  //   }
  // };

  const handlePageChange = (page: number) => {
    updateParams({ currentPage: page });
    setPage(page);
  };

  const handleLimitChange = (limit: number) => {
    updateParams({ pageSize: limit, currentPage: 1 });
    setLimit(limit);
  };

  return (
    <div className="space-y-4">
      <h2>Transaction Logs</h2>

      {isLoading ? (
        <div className="flex h-full items-center justify-center">
          <Loading />
        </div>
      ) : isError ? (
        <div className="py-8 text-center text-red-500">
          Error loading transactions
        </div>
      ) : (
        <>
          {transactions.length > 0 ? (
            <div className="space-y-4">
              <Accordion
                type="multiple"
                value={openItems}
                onValueChange={setOpenItems}
              >
                {transactions.map((transaction) => (
                  <AccordionItem key={transaction.id} value={transaction.id}>
                    <AccordionTrigger className="hover:bg-sidebar-accent cursor-pointer rounded-none px-4 hover:no-underline">
                      <div className="flex w-full items-center justify-between">
                        <div className="flex items-center gap-4">
                          <span className="font-medium">
                            ID: {transaction.id}
                          </span>
                          <span className="text-muted-foreground">
                            {format(
                              new Date(transaction.createdAt),
                              "dd MMM yyyy HH:mm",
                            )}
                          </span>
                        </div>
                        <div className="flex items-center gap-4">
                          <span className="font-medium">
                            {transaction.checkOutAmount?.toFixed(2)}{" "}
                            {transaction.currency.currencyCode}
                          </span>
                          <Badge
                            variant={
                              transaction.paymentTransactionList[0]?.status ===
                              "SUCCESS"
                                ? "success"
                                : "destructive"
                            }
                          >
                            {transaction.paymentTransactionList[0]?.status ||
                              "PENDING"}
                          </Badge>
                        </div>
                      </div>
                    </AccordionTrigger>
                    <AccordionContent>
                      <div className="space-y-6 p-4">
                        <div className="grid grid-cols-2 gap-4 md:grid-cols-3">
                          <div>
                            <p className="text-muted-foreground text-sm">
                              Reference ID
                            </p>
                            <p className="font-medium">
                              {transaction.paymentReferenceId}
                            </p>
                          </div>
                          {transaction.description && (
                            <div className="col-span-2">
                              <p className="text-muted-foreground text-sm">
                                Description
                              </p>
                              <p>{transaction.description}</p>
                            </div>
                          )}
                        </div>

                        <div>
                          <h4 className="mb-2 font-semibold">
                            Payment Transactions
                          </h4>
                          <Table>
                            <TableHeader>
                              <TableRow>
                                <TableHead>Channel</TableHead>
                                <TableHead>Status</TableHead>
                                <TableHead className="text-right">
                                  Amount
                                </TableHead>
                                <TableHead className="text-right">
                                  Date
                                </TableHead>
                              </TableRow>
                            </TableHeader>
                            <TableBody>
                              {transaction?.paymentTransactionList?.map(
                                (payment) => (
                                  <TableRow key={payment.id}>
                                    <TableCell>
                                      {payment.createdBy || "N/A"}
                                    </TableCell>
                                    <TableCell>
                                      <Badge
                                        variant={
                                          payment.status === "SUCCESS"
                                            ? "success"
                                            : "destructive"
                                        }
                                      >
                                        {payment.status}
                                      </Badge>
                                    </TableCell>
                                    <TableCell className="text-right">
                                      {prettyFixedAmount(payment.amount)}{" "}
                                      {transaction.currency.currencyCode}
                                    </TableCell>
                                    <TableCell className="text-right">
                                      {format(
                                        new Date(payment.createdAt),
                                        "dd MMM yyyy HH:mm",
                                      )}
                                    </TableCell>
                                  </TableRow>
                                ),
                              )}
                            </TableBody>
                          </Table>
                        </div>
                      </div>
                    </AccordionContent>
                  </AccordionItem>
                ))}
              </Accordion>

              {/* <CommonCursorPagination
            pagination={meta}
            onPaginationChange={handlePaginationChange}
          /> */}

              {meta && (
                <CommonPagination
                  page={meta.currentPage}
                  totalPages={meta.totalPages}
                  limit={meta.pageSize}
                  hasNextPage={meta.hasNextPage}
                  hasPrevPage={meta.hasPreviousPage}
                  onPageChange={handlePageChange}
                  onLimitChange={handleLimitChange}
                  limitOptions={[10, 25, 50, 100]}
                  showLimitSelector={true}
                />
              )}
            </div>
          ) : (
            <div className="flex h-42 items-center justify-center">
              <p>There is no data</p>
            </div>
          )}
        </>
      )}
    </div>
  );
};

export default ViewLogsPage;
